import { Model_ConfigurationsGet, Model_LabelAndReferenceGet } from "src/backend/openapi/v1";
import { TGroupFieldKey, TGroupNode, TGroupSortBy, TSortDirection } from "../../configurator-types";

export type TConfigurationEntity = Model_ConfigurationsGet["configurations"][number];

// The name columns are sized once per list and handed to the rows as CSS custom properties: the
// list is virtualized and the rows sit behind accordion/tree wrappers, so no shared layout box
// (grid, subgrid, display:contents) can reach them — but custom properties inherit regardless.
export const CONFIGURATION_NAME_WIDTH_VARIABLE = "--dekox-configuration-name-width";
export const PRODUCT_NAME_WIDTH_VARIABLE = "--dekox-product-name-width";

// Used until the list has been measured, and as the floor afterwards, so the column can only ever
// grow from what it is today — nothing that fits now can start truncating.
export const CONFIGURATION_NAME_FALLBACK_WIDTH = 280;
export const PRODUCT_NAME_FALLBACK_WIDTH = 250;

// Safety ceiling on the name column as a share of the list width. The row-overhead estimates below
// are approximate, so this stops an underestimate from letting the name push the actions off-row.
export const NAME_COLUMN_MAX_FRACTION = 0.4;

// Everything in a row other than the name and chips columns, which the name column must not eat
// into. Estimated from the layout in configuration.tsx / product-configuration.tsx and rounded up
// deliberately: overestimating costs the name a few pixels, underestimating costs a row a chip.
// Configuration row = checkbox 38 + chevron 24 + product count 100 + status slot 72 + 3 icon buttons 90.
export const CONFIGURATION_ROW_OVERHEAD = 340;

// Product row = row padding 24 + checkbox 24 + three 16px gaps + status slot 72, plus the
// AccordionDetails padding and the TreeItem's own chevron column.
export const PRODUCT_ROW_BASE_OVERHEAD = 250;

// Per nesting level: the groupTransition's pl:2 plus --TreeView-itemChildrenIndentation.
export const PRODUCT_ROW_DEPTH_INDENT = 28;

// The Products tab renders the same row flat (no accordion, no tree), so it reserves less.
export const PRODUCTS_TAB_ROW_OVERHEAD = 200;

type TConfigurationGroupNode = TGroupNode<TConfigurationEntity>;

// One level's unsorted buckets, before group-sort is applied and before deciding whether to recurse further
type TConfigurationBucket = { key: string; label: string; configurations: TConfigurationEntity[] };

type TGroupTranslations = {
  groupByMissingInfoYes: string;
  groupByMissingInfoNo: string;
  groupByUnlabelled: string;
};

// Partitions configurations into unsorted buckets for exactly one field — the reusable per-level
// step that groupConfigurationsHierarchy recurses with. label grouping: a configuration with N
// labels appears in N buckets; no-label → "unlabelled" bucket.
function partitionConfigurationsOnce(
  configurations: TConfigurationEntity[],
  groupField: TGroupFieldKey,
  labelIndex: Map<string, Model_LabelAndReferenceGet[]>,
  t: TGroupTranslations
): TConfigurationBucket[] {
  const groupMap = new Map<string, TConfigurationBucket>();

  const addToGroup = (key: string, label: string, configuration: TConfigurationEntity) => {
    if (!groupMap.has(key)) groupMap.set(key, { key, label, configurations: [] });
    const group = groupMap.get(key);
    if (!group) throw new Error("Group was just set but is missing from groupMap");
    group.configurations.push(configuration);
  };

  for (const configuration of configurations) {
    if (groupField === "configurationLabel") {
      // systemProductConfigurationId is the configuration key in the list-view model
      const configLabels = labelIndex.get(configuration.systemProductConfigurationId ?? "") ?? [];
      if (configLabels.length === 0) {
        addToGroup("unlabelled", t.groupByUnlabelled, configuration);
      } else {
        for (const lbl of configLabels) addToGroup(lbl.shortname, lbl.shortname, configuration);
      }
    } else if (groupField === "missingInfo") {
      const hasMissing = (configuration.missingInformation?.length ?? 0) > 0;
      const key = hasMissing ? "yes" : "no";
      addToGroup(key, hasMissing ? t.groupByMissingInfoYes : t.groupByMissingInfoNo, configuration);
    } else if (groupField === "invalidInfo") {
      // reuse missingInfo bucket labels (Yes/No) — no separate invalidInfo bucket keys needed, same as products-utils.ts
      const hasInvalid = (configuration.invalidInformation?.length ?? 0) > 0;
      const key = hasInvalid ? "yes" : "no";
      addToGroup(key, hasInvalid ? t.groupByMissingInfoYes : t.groupByMissingInfoNo, configuration);
    }
  }

  return Array.from(groupMap.values());
}

// Orders a single level's buckets by the user's group-sort setting — no "date" special case here,
// unlike products-utils.ts, since the Configurations tab has no date group-by option.
function sortConfigurationBuckets(buckets: TConfigurationBucket[], groupSortBy: TGroupSortBy, groupSortDirection: TSortDirection): TConfigurationBucket[] {
  const compare =
    groupSortBy === "count"
      ? (a: TConfigurationBucket, b: TConfigurationBucket) => a.configurations.length - b.configurations.length
      : (a: TConfigurationBucket, b: TConfigurationBucket) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" });

  const sorted = [...buckets].sort(compare);
  return groupSortDirection === "asc" ? sorted : sorted.reverse();
}

// Recursively groups configurations into a multi-level hierarchy, one level per entry in
// groupFields (top-level first) — same pattern as groupProductsHierarchy in products-utils.ts.
export function groupConfigurationsHierarchy(
  configurations: TConfigurationEntity[],
  groupFields: TGroupFieldKey[],
  labelIndex: Map<string, Model_LabelAndReferenceGet[]>,
  t: TGroupTranslations,
  groupSortBy: TGroupSortBy,
  groupSortDirection: TSortDirection,
  depth = 0
): TConfigurationGroupNode[] {
  const [field, ...restFields] = groupFields;
  const buckets = sortConfigurationBuckets(partitionConfigurationsOnce(configurations, field, labelIndex, t), groupSortBy, groupSortDirection);

  return buckets.map(bucket =>
    restFields.length === 0
      ? { key: bucket.key, label: bucket.label, depth, items: bucket.configurations }
      : { key: bucket.key, label: bucket.label, depth, children: groupConfigurationsHierarchy(bucket.configurations, restFields, labelIndex, t, groupSortBy, groupSortDirection, depth + 1) }
  );
}
